import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Breadcrumbs, BreadcrumbItem } from '@vnetwork/ui/layout';

/** A single linked breadcrumb ancestor. */
export interface DetailCrumb {
  label: ReactNode;
  to: string;
}

export interface DetailHeaderProps {
  /**
   * Breadcrumb ancestor(s) before the current page. Pass one crumb for the
   * common `List › entity` trail, or an array for nested trails such as
   * `Services › Customer › service`. Omit for a top-level detail.
   */
  parent?: DetailCrumb | DetailCrumb[];
  /** Breadcrumb current-page label (the entity name / code). */
  current: ReactNode;
  /** Muted `#id` prefix rendered before the title. */
  idPrefix?: ReactNode;
  /** Main title — the entity name or code (semibold). */
  title: ReactNode;
  /**
   * Inline meta after the title — typically the dominant `StatusPill` plus
   * subtle `Chip`s. Rendered in the same flex row so pills sit next to the name.
   */
  meta?: ReactNode;
  /** Right-aligned lifecycle actions (approve / cancel / edit menus). */
  actions?: ReactNode;
}

/**
 * The standard detail-page identity header shared by every domain
 * (customer / invoice / contract / service / operations / …): a breadcrumb
 * trail, then a title row with an optional muted `#id` prefix, the entity name,
 * inline status/chip meta, and right-aligned actions.
 *
 * This replaces the per-domain `*-detail-header.tsx` copies so all detail pages
 * render an identical frame — pass domain-specific pills/chips via `meta` and
 * lifecycle buttons via `actions`.
 */
export function DetailHeader({
  parent,
  current,
  idPrefix,
  title,
  meta,
  actions,
}: DetailHeaderProps) {
  const parents = parent ? (Array.isArray(parent) ? parent : [parent]) : [];
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      <Breadcrumbs>
        {parents.map((crumb, i) => (
          <BreadcrumbItem key={i} asChild>
            <Link to={crumb.to}>{crumb.label}</Link>
          </BreadcrumbItem>
        ))}
        <BreadcrumbItem current>{current}</BreadcrumbItem>
      </Breadcrumbs>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--vn-space-3)',
          flexWrap: 'wrap',
        }}
      >
        <h1
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 'var(--vn-space-2)',
            fontSize: 'var(--vn-font-size-xl)',
            fontWeight: 'var(--vn-font-weight-semibold)',
            margin: 0,
            minWidth: 0,
            overflowWrap: 'anywhere',
          }}
        >
          {idPrefix ? (
            <span
              style={{
                color: 'var(--vn-fg-muted)',
                fontSize: 'var(--vn-font-size-sm)',
                fontWeight: 'var(--vn-font-weight-regular)',
              }}
            >
              {idPrefix}
            </span>
          ) : null}
          <span>{title}</span>
        </h1>
        {meta}
        {actions ? <div style={{ marginLeft: 'auto' }}>{actions}</div> : null}
      </div>
    </div>
  );
}
